(function () {
  /**
   * Represents a printer choice.
   */
  class PrinterChoice {
    /**
     * Creates a new PrinterChoice instance.
     * @param {string} id - The unique identifier of the printer choice.
     * @param {string} name - The name of the printer choice.
     * @param {string} image - The image associated with the printer choice.
     * @param {string} category - The category of the printer choice.
     * @param {Object} options - Additional options for the printer choice.
     */
    constructor(id, name, image, category, options) {
      this.id = id;
      this.name = name;
      this.image = image;
      this.category = category;
      this.options = options;
    }
  }

  /**
   * List of default choices
   */
  var FULL_COLOR = "Full Color";
  var INK_SAVER = "Ink Saver";
  var ADVANCED = "advanced";
  var SIMPLE = "simple";
  var defaultChoices = [
    new PrinterChoice("color-grid", FULL_COLOR, "advanced-color", ADVANCED, {
      tabletype: "grid",
      printfriendlyMap: false,
    }),
    new PrinterChoice(
      "printfriendly-grid",
      INK_SAVER,
      "advanced-bw",
      ADVANCED,
      {
        tabletype: "grid",
        printfriendlyMap: true,
      },
    ),
    new PrinterChoice("color-list", FULL_COLOR, "simple-color", SIMPLE, {
      tabletype: "list",
      printfriendlyMap: false,
    }),
    new PrinterChoice("printfriendly-list", INK_SAVER, "simple-bw", SIMPLE, {
      tabletype: "list",
      printfriendlyMap: true,
    }),
  ];

  /**
   * List of route choices
   */
  var routeChoices = defaultChoices.map((choice) => {
    /**
     * @warning If moving away from class instances, make sure to deep copy the default choice.
     * Instantiating a new PrinterChoice as we are here creates a distinct object in memory.
     */
    const newRouteChoice = new PrinterChoice(
      "route-" + choice.id,
      choice.name,
      choice.image,
      choice.category,
      {
        ...choice.options,
        hideUnselectedTableRows: true,
      },
    );
    return newRouteChoice;
  });

  /**
   * PrinterChoices - A simple class that only returns the choices for printing
   * @method getPrintChoices returns a list of printer choices.
   * @method getSupportedModes returns a list of supported modes.
   * @method setMode sets the mode of printer choices.
   * @method choiceToHtml converts a printer choice object into an HTML representation.
   * @method getPrinterChoice finds a printer choice by id.
   */
  class PrinterChoices {
    constructor() {
      /**
       * Object containing printer choices indexed by mode.
       * @type {object}
       */
      this.printerChoicesByMode = {
        default: defaultChoices,
        route: routeChoices,
      };

      /**
       * List of supported modes.
       * @type {string[]}
       */
      this.supportedModes = Object.keys(this.printerChoicesByMode);

      /**
       * Currently selected printer choices.
       * @type {array}
       */
      this.printerChoices = this.printerChoicesByMode.default;
    }

    /**
     * Returns the list of printer choices.
     * @returns {array} List of printer choices.
     */
    getPrinterChoices() {
      return this.printerChoices;
    }

    /**
     * Returns the list of supported modes.
     * @returns {string[]} List of supported modes.
     */
    getSupportedModes() {
      return this.supportedModes;
    }

    /**
     * Sets the mode of printer choices.
     * @param {string} [mode='default'] - The mode to set.
     */
    setMode(mode = "default") {
      this.printerChoices =
        this.printerChoicesByMode[mode] || this.printerChoicesByMode.default;
    }

    /**
     * Converts a printer choice object into an HTML representation.
     * @param {Object} choice - The printer choice object.
     * @returns {string} HTML representation of the printer choice.
     */
    choiceToHtml(choice) {
      var imgUrl = 'url("/images/print/' + choice.image + '.png")';
      var subTitle = choice.name;

      var icon = $("<div>")
        .addClass("printer-dialog-choice-icon")
        .css({ "background-image": imgUrl });

      var iconHtml = $("<div>").append(icon.clone()).html();

      return `
				<li class="printer-dialog-choice-frame" data-id="${choice.id}">
					<div class="printer-dialog-choice">${iconHtml}</div>
					<p class="printer-dialog-choice-subtitle">${subTitle}</p>
				</li>
			`;
    }

    /**
     * Finds a printer choice by its ID.
     * @param {string} id - The ID of the printer choice to find.
     * @returns {Object} The corresponding printer choice object, or the default choice if not found.
     */
    getPrinterChoice(id) {
      function choiceById(choice) {
        return id == choice.id;
      }
      return (
        this.printerChoices.filter(choiceById)[0] || this.printerChoices[0]
      );
    }
  }

  window.PrinterChoices = PrinterChoices;
  // Do not remove. This is needed for access in '/js/runner/' testing files.
  window.PrinterChoice = PrinterChoice;
})();
